import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";

export const AdministratorMyShortedUnmappedEvents = () => {
    const dispatch = useDispatch();

    const [myShortedUnmappedEvents, setMyShortedUnmappedEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [onlyMappedVenues, setOnlyMappedVenues] = useState(false);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedPOSEventId, setLastSelectedPOSEventId] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 20,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };

    const DeleteCell = (props) => {
        const posEventId = props.dataItem.posEventId;

        return (
            <td className="text-nowrap">
                <Button type={"button"} icon="trash" title="Delete" onClick={() => deleteEvent(posEventId)}>
                    Delete
                </Button>
            </td>
        );
    };

    const SelectedCell = (props) => {
        const posEventId = props.dataItem.posEventId;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, posEventId, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const getMyShortedUnmappedEvents = () => {
        setIsLoading(true);

        fetch(`/api/administrator/pos/myshortedunmappedevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setMyShortedUnmappedEvents(result);
                setTotalEventCount(result.length);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);                
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const onlyMappedVenuesChanged = (event) => {
        setOnlyMappedVenues(event.value);
        setPage(initialPageState);

        if (!event.value) {
            setTotalEventCount(myShortedUnmappedEvents.length);
        }
        else {
            let myShortedUnmappedEventsCopy = myShortedUnmappedEvents.filter(element => element.isVenueMapped === true);
            setTotalEventCount(myShortedUnmappedEventsCopy.length);
        }        
    };

    const getGridData = () => {
        if (!onlyMappedVenues) {
            return myShortedUnmappedEvents.slice(page.skip, page.take + page.skip);
        }

        let myShortedUnmappedEventsCopy = myShortedUnmappedEvents.filter(element => element.isVenueMapped === true);

        return myShortedUnmappedEventsCopy.slice(page.skip, page.take + page.skip);
    };

    const deleteEvent = (posEventId) => {
        if (window.confirm("Are you sure you want to delete this event?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: [posEventId],
        };

        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Event deleted from POS successfully");

                getMyShortedUnmappedEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const deleteEvents = (posEventIds) => {
        if (window.confirm("Are you sure you want to delete these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: posEventIds,
        };

        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted from POS successfully");

                getMyShortedUnmappedEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const selectedValueChanged = (event, posEventId, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let myShortedUnmappedEventsCopy = myShortedUnmappedEvents;

            const itemIndex = myShortedUnmappedEventsCopy.findIndex(dataItem =>
                dataItem.posEventId === posEventId
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedPOSEventId !== undefined
                && lastSelectedPOSEventId !== posEventId) {
                const lastSelectedItemIndex = myShortedUnmappedEventsCopy.findIndex(dataItem =>
                    dataItem.posEventId === lastSelectedPOSEventId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        myShortedUnmappedEventsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                myShortedUnmappedEventsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedPOSEventId(posEventId);
            }

            setMyShortedUnmappedEvents(myShortedUnmappedEventsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let myShortedUnmappedEventsCopy = myShortedUnmappedEvents;
            
            myShortedUnmappedEventsCopy.forEach(dataItem =>
                dataItem.selected = false
            );

            if (checked === true) {
                const gridData = getGridData();

                myShortedUnmappedEventsCopy.forEach(dataItem => {
                    const exists = gridData.includes(dataItem);

                    if (exists) {
                        dataItem.selected = true
                    }                   
                });
            }            

            setMyShortedUnmappedEvents(myShortedUnmappedEventsCopy);

            setLastSelectedPOSEventId(undefined);

            setIsLoading(false);
        }, 1);
    };

    const deleteSelectedEvents = () => {
        let posEventIds = [];

        myShortedUnmappedEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                posEventIds.push(dataItem.posEventId);
            }
        });

        if (posEventIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        deleteEvents(posEventIds);
    };

    useEffect(() => {
        document.title = "My Shorted Unmapped Events";

        getMyShortedUnmappedEvents();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></> }

            <div className="k-form k-form-md">
                <div className="k-form-fieldset container">
                    <div className="row">
                        <div className="col-12">
                            <div className="k-form-field">
                                <Checkbox
                                    label={"Only show events with mapped venues"}
                                    onChange={onlyMappedVenuesChanged}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="m-2">
                <Button type={"button"} icon="trash" title="Delete Selected Events" onClick={deleteSelectedEvents}>
                    Delete Selected Events
                </Button>
            </div>   

            <Grid
                data={getGridData()}
                resizable={true}
                skip={page.skip}
                take={page.take}
                total={totalEventCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column cell={DeleteCell} />
                <Column field="posEventId" title={"Id"} />
                <Column field="name" title={"Event"} />
                <Column field="eventDateAsString" title={"Date"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="venueCity" title={"City"} />
                <Column field="venueRegion" title={"Region"} />
                <Column field="venueCountryCode" title={"Country"} />
                <Column field="isVenueMapped" title={"Venue mapped"} />
            </Grid>
        </div>
    );
}